import { forwardRef, ForwardRefRenderFunction, HTMLAttributes, useCallback, useMemo, useEffect } from 'react'

import classNames from 'classnames'
import { useRouter } from 'next/router'
import PullToRefresh from 'react-simple-pull-to-refresh'

import { useProfileDoneModal } from '@web/components/Modal/ProfileDone/useProfileDoneModal'
import useProfileResetModal from '@web/components/Modal/ProfileReset/useProfileResetModal'
import { UnavailableType } from '@web/components/Modal/UnavailableUser/userModal.constant'
import { useUnavailableUserModal } from '@web/components/Modal/UnavailableUser/useUnavailableUserModal'
import PhaseLabel from '@web/components/PhaseLabel'
import TopButton from '@web/components/TopButton/TopButton'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import routes from '@web/libs/constants/routes'
import { isMatchPath } from '@web/libs/matchPath'

import MetaTags, { MetaTagsProp } from './Document/MetaTags'
import styles from './Layout.module.scss'
import Loading from './Loading'

interface Props extends HTMLAttributes<HTMLDivElement> {
  metaTagProps?: MetaTagsProp
  /**
   * https://developer.mozilla.org/en-US/docs/Web/CSS/overflow-anchor
   */
  disableOverFlowAnchor?: boolean
}

const Layout: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  { children, className, metaTagProps, disableOverFlowAnchor = false, ...props },
  ref,
) => {
  const router = useRouter()
  const { isFromMapsApp, isSsr } = useGlobalStaticStateContext()

  const { openUnavailableUserModal } = useUnavailableUserModal()

  useEffect(() => {
    openUnavailableUserModal(UnavailableType.ENTRY_CHECK)
  }, [openUnavailableUserModal])

  useProfileDoneModal()
  useProfileResetModal()

  const isPullablePath = !isMatchPath(router.asPath, {
    path: [routes.THEME_MAP, routes.VISIT_DETAIL],
  })
  const isPullable = useMemo(() => {
    return isFromMapsApp && isPullablePath && !isSsr
  }, [isFromMapsApp, isPullablePath, isSsr])

  const handleRefresh = useCallback(async () => {
    window.location.reload()
  }, [])

  return isPullablePath ? (
    <PullToRefresh
      onRefresh={handleRefresh}
      isPullable={isPullable}
      pullingContent=""
      refreshingContent={isPullable ? <Loading /> : undefined}
      className="place_pull_to_refresh"
    >
      <>
        <div
          ref={ref}
          className={classNames({ [styles.overflow_anchor_none]: disableOverFlowAnchor }, className)}
          {...props}
        >
          <MetaTags {...metaTagProps} />
          {children}
        </div>
        <TopButton />
        <PhaseLabel />
      </>
    </PullToRefresh>
  ) : (
    <div
      ref={ref}
      className={classNames({ [styles.overflow_anchor_none]: disableOverFlowAnchor }, className)}
      {...props}
    >
      <MetaTags {...metaTagProps} />
      {children}
      <TopButton />
      <PhaseLabel />
    </div>
  )
}

export default forwardRef(Layout)
